import { supabase } from '../lib/supabase';
import { MetaOAuthConnection, MetaLead, MetaLeadFilters, MetaLeadStats } from '../types';

/**
 * Service pour gérer l'intégration Meta Lead Ads
 */
export const metaService = {
  /**
   * Initie le flow OAuth Meta
   * Redirige vers Meta pour l'autorisation
   */
  initiateOAuth: async (): Promise<{ authUrl: string } | { error: any }> => {
    try {
      const { data, error } = await supabase.functions.invoke('meta-oauth-init', {
        body: {}
      });

      if (error) {
        console.error('Erreur lors de l\'initiation OAuth Meta', error);
        return { error };
      }

      return { authUrl: data.authUrl };
    } catch (e: any) {
      console.error('Erreur critique lors de l\'initiation OAuth Meta', e);
      return { error: e.message || 'Erreur inconnue' };
    }
  },

  /**
   * Termine le flow OAuth (appel POST vers l'Edge Function) après redirection browser.
   * - 1er appel (sans pageId): retourne la liste des pages
   * - 2e appel (avec pageId): enregistre la connexion
   */
  completeOAuth: async (
    code: string,
    state?: string | null,
    pageId?: string
  ): Promise<
    | { pages: Array<{ id: string; name: string }>; tempToken?: string; expiresAt?: string }
    | { success: true; connection: { id: string; pageId: string; pageName: string } }
    | { error: any }
  > => {
    try {
      const { data, error } = await supabase.functions.invoke('meta-oauth-callback', {
        body: { code, state, pageId }
      });

      if (error) {
        console.error('Erreur lors du callback OAuth Meta', error);
        return { error };
      }

      return data;
    } catch (e: any) {
      console.error('Erreur critique lors du callback OAuth Meta', e);
      return { error: e.message || 'Erreur inconnue' };
    }
  },

  /**
   * Termine le flow OAuth en utilisant un token temporaire (au lieu du code).
   * Utilisé lors de la confirmation de la page pour éviter de réutiliser le code.
   */
  completeOAuthWithToken: async (
    tempToken: string,
    pageId: string
  ): Promise<
    | { success: true; connection: { id: string; pageId: string; pageName: string } }
    | { error: any }
  > => {
    try {
      const { data, error } = await supabase.functions.invoke('meta-oauth-callback', {
        body: { tempToken, pageId }
      });

      if (error) {
        console.error('Erreur lors du callback OAuth Meta avec token', error);
        return { error };
      }

      return data;
    } catch (e: any) {
      console.error('Erreur critique lors du callback OAuth Meta avec token', e);
      return { error: e.message || 'Erreur inconnue' };
    }
  },

  /**
   * Récupère la connexion OAuth active
   */
  getConnection: async (): Promise<MetaOAuthConnection | null> => {
    try {
      const { data, error } = await supabase
        .from('meta_oauth_connections')
        .select('*')
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        console.error('Erreur lors de la récupération de la connexion Meta', error);
        return null;
      }

      if (!data) return null;

      // Mapper depuis snake_case DB vers camelCase
      return {
        id: data.id,
        pageId: data.page_id,
        pageName: data.page_name,
        accessToken: data.access_token,
        longLivedToken: data.long_lived_token,
        tokenExpiresAt: data.token_expires_at,
        refreshToken: data.refresh_token,
        webhookSecret: data.webhook_secret,
        isActive: data.is_active,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };
    } catch (e: any) {
      console.error('Erreur critique lors de la récupération de la connexion Meta', e);
      return null;
    }
  },

  /**
   * Déconnecte Meta (désactive la connexion)
   */
  disconnect: async (): Promise<{ error?: any }> => {
    try {
      const { error } = await supabase
        .from('meta_oauth_connections')
        .update({ is_active: false })
        .eq('is_active', true);

      if (error) {
        console.error('Erreur lors de la déconnexion Meta', error);
        return { error };
      }

      return {};
    } catch (e: any) {
      console.error('Erreur critique lors de la déconnexion Meta', e);
      return { error: e.message || 'Erreur inconnue' };
    }
  },

  /**
   * Rafraîchit le token long terme (appelé via Edge Function)
   */
  refreshToken: async (): Promise<{ error?: any }> => {
    try {
      const { error } = await supabase.functions.invoke('meta-refresh-token', {
        body: {}
      });

      if (error) {
        console.error('Erreur lors du rafraîchissement du token Meta', error);
        return { error };
      }

      return {};
    } catch (e: any) {
      console.error('Erreur critique lors du rafraîchissement du token Meta', e);
      return { error: e.message || 'Erreur inconnue' };
    }
  }
};
